import CardHeader from "components/CardHeader";
import FilterToggleButton from "components/FilterToggleButton";
import PageMeta from "components/PageMeta";
import usePersistentFilterSet from "common/usePersistentFilterSet";
import { Account } from "models/Account";
import { Person } from "models/Person";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Col, Row } from "reactstrap";
import { bindActionCreators } from "redux";
import * as api from "services/Api";
import { getPersons } from "store/actions/Customers";
import { PersonsState } from "store/reducers/Persons";
import { RootState } from "../../store/store";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    personState: PersonsState;
}

interface Actions {
    getPersons: () => any;
}

type PersonStatus = "without-login" | "without-mfa" | "with-mfa";

const PersonStatuses: PersonStatus[] = ["without-login", "without-mfa", "with-mfa"];

const getPersonStatus = (person: Person): PersonStatus => {
    if (!person.auth) {
        return "without-login";
    }

    return person.auth.mfa_active ? "with-mfa" : "without-mfa";
};

const initials = (firstName: string, lastName: string) =>
    `${firstName?.charAt(0) ?? ""}${lastName?.charAt(0) ?? ""}`.toUpperCase() || "?";

const getLinkedAccounts = (personId: string, accounts: Account[]) =>
    accounts.filter(account => (account.persons ?? []).some(link => link.person_id === personId));

export const Persons: React.FC<Props & Actions> = ({
    personState,
    getPersons,
}) => {
    const { t } = useOfficeTranslation();
    const [keyword, setKeyword] = useState("");
    const [visibleStatuses, toggleStatus] = usePersistentFilterSet(
        "galactic-filters-persons-statuses",
        PersonStatuses,
    );
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [accounts, setAccounts] = useState<Account[]>([]);

    useEffect(() => {
        Promise.all([
            Promise.resolve(getPersons()),
            api.getAccounts().then(setAccounts),
        ]).catch(() => setLoadFailed(true)).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const persons = personState.getPersons();
    const matchingPersons = useMemo(() => {
        const query = keyword.trim().toLowerCase();

        return [...persons]
            .filter(person => {
                const accountNames = getLinkedAccounts(person.id, accounts)
                    .map(account => account.legal_name)
                    .join(" ");
                const matchesQuery = !query || [
                    person.name,
                    person.last_name,
                    person.email,
                    person.phone,
                    person.title,
                    accountNames,
                ].some(value => (value ?? "").toLowerCase().includes(query));

                return matchesQuery;
            })
            .sort((a, b) => `${a.name} ${a.last_name}`.localeCompare(`${b.name} ${b.last_name}`));
    }, [persons, accounts, keyword]);

    const statusCounts = useMemo(
        () => matchingPersons.reduce<Record<PersonStatus, number>>(
            (counts, person) => ({
                ...counts,
                [getPersonStatus(person)]: counts[getPersonStatus(person)] + 1,
            }),
            { "without-login": 0, "without-mfa": 0, "with-mfa": 0 },
        ),
        [matchingPersons],
    );

    const filteredPersons = useMemo(
        () => matchingPersons.filter(person => visibleStatuses.has(getPersonStatus(person))),
        [matchingPersons, visibleStatuses],
    );

    return (
        <div className="page-content">
            <PageMeta title={t("common.persons")} />

            <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <h4 className="mb-1">{t("common.persons")}</h4>
                    <p className="text-muted mb-0">{t("persons.persons.manageContactsAndTheirLinkedRelations")}</p>
                </div>
            </div>

            {loadFailed && <Alert color="danger">{t("persons.persons.couldNotLoadPersons")}</Alert>}

            <Card>
                <CardBody>
                    <CardHeader
                        title={t("persons.persons.personOverview")}
                        icon="mdi-account-multiple-outline"
                        count={filteredPersons.length}
                    />

                    <Row className="g-3 py-3 border-bottom">
                        <Col md={6}>
                            <div className="position-relative">
                                <input
                                    aria-label={t("persons.persons.searchPersons")}
                                    className="form-control ps-5"
                                    placeholder={t("persons.persons.searchByNameEmailPhoneOrRelation")}
                                    value={keyword}
                                    onChange={event => setKeyword(event.target.value)}
                                />
                                <i
                                    className="mdi mdi-magnify position-absolute text-muted font-size-20"
                                    style={{ left: 16, top: 7 }}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-2 h-100">
                                <FilterToggleButton
                                    active={visibleStatuses.has("without-login")}
                                    count={statusCounts["without-login"]}
                                    label={t("persons.persons.withoutLogin")}
                                    onToggle={() => toggleStatus("without-login")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("without-mfa")}
                                    count={statusCounts["without-mfa"]}
                                    label={t("persons.persons.withoutMfa")}
                                    onToggle={() => toggleStatus("without-mfa")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("with-mfa")}
                                    count={statusCounts["with-mfa"]}
                                    label={t("persons.persons.withMfa")}
                                    onToggle={() => toggleStatus("with-mfa")}
                                />
                            </div>
                        </Col>
                    </Row>

                    {loading && persons.length === 0 &&
                        <p className="text-muted text-center py-5 mb-0">{t("persons.persons.loadingPersons")}</p>
                    }

                    {filteredPersons.map(person => {
                        const linkedAccounts = getLinkedAccounts(person.id, accounts);
                        const customerAccount = linkedAccounts.find(account => account.roles.includes("customer"));

                        return <div className="border-bottom py-3" key={person.id}>
                            <Row className="align-items-center g-3">
                                <Col md={3}>
                                    <Link
                                        className="d-flex align-items-center text-body text-decoration-none"
                                        to={`/persons/${person.id}`}
                                    >
                                        <span
                                            className="avatar-sm me-3 flex-shrink-0"
                                            style={{ width: 36, minWidth: 36 }}
                                        >
                                            <span className="avatar-title rounded-circle bg-primary bg-soft text-primary fw-semibold">
                                                {initials(person.name, person.last_name)}
                                            </span>
                                        </span>
                                        <span>
                                            <strong>{person.name} {person.last_name}</strong>
                                            {person.title &&
                                                <small className="d-block text-muted">{person.title}</small>
                                            }
                                        </span>
                                    </Link>
                                </Col>
                                <Col md={3}>
                                    {person.email
                                        ? <Link
                                            className="d-block"
                                            to={`/conversations/compose?person=${person.id}${customerAccount
                                                ? `&customer=${customerAccount.id}`
                                                : ""
                                            }`}
                                        >
                                            {person.email}
                                        </Link>
                                        : <span className="text-muted">{t("common.noEmail")}</span>
                                    }
                                    {person.phone &&
                                        <a className="d-block text-muted" href={`tel:${person.phone}`}>
                                            {person.phone}
                                        </a>
                                    }
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex flex-wrap gap-1">
                                        {linkedAccounts.flatMap(account => account.roles.length > 0
                                            ? account.roles.map(role =>
                                                <Link
                                                    className={`badge ${role === "customer"
                                                        ? "bg-primary bg-soft text-primary"
                                                        : "bg-warning bg-soft text-warning"
                                                    }`}
                                                    key={`${account.id}-${role}`}
                                                    to={`/accounts/${account.id}`}
                                                >
                                                    {account.legal_name} · {t(role === "customer" ? "common.customer" : "common.supplier").toLowerCase()}
                                                </Link>
                                            )
                                            : [
                                            <Link
                                                className="badge bg-secondary bg-soft text-secondary"
                                                key={account.id}
                                                to={`/accounts/${account.id}`}
                                            >
                                                {account.legal_name}
                                            </Link>
                                            ]
                                        )}
                                        {linkedAccounts.length === 0 &&
                                            <span className="text-muted">{t("persons.persons.noRelations")}</span>
                                        }
                                    </div>
                                </Col>
                                <Col md={2}>
                                    {person.auth === null
                                        ? <span
                                            className="badge bg-light text-muted"
                                            title={t("persons.persons.theAuthenticationServiceReturnedNoStatus")}
                                        >
                                            {t("persons.persons.statusUnavailable")}
                                        </span>
                                        : person.auth.mfa_active
                                            ? <span className="badge bg-success bg-soft text-success">{t("common.mfaActive")}</span>
                                            : <span className="badge bg-warning bg-soft text-warning">{t("common.mfaInactive")}</span>
                                    }
                                </Col>
                                <Col className="text-end" md={1}>
                                    <Link
                                        aria-label={t("persons.persons.viewPerson", {name: person.name})}
                                        className="btn btn-sm btn-light"
                                        to={`/persons/${person.id}`}
                                    >
                                        <i className="mdi mdi-chevron-right" />
                                    </Link>
                                </Col>
                            </Row>
                        </div>;
                    })}

                    {!loadFailed && (!loading || persons.length > 0) && filteredPersons.length === 0 &&
                        <p className="text-muted text-center mt-3 mb-0">{t("persons.persons.noPersonsFound")}</p>
                    }
                </CardBody>
            </Card>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    personState: state.Persons,
});
const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getPersons }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Persons);
